import React, { useState, useEffect, useContext } from 'react';
import { string, object } from 'prop-types';
import { Flex } from 'rebass';
import { SortAscending, SortDescending, Filter } from '@nike/nike-design-system-icons';

import { sortHighlightColor, filterHightlightColor } from './constants';

import ColumnFilterSortMenu from './ColumnFilterSortMenu';

import { TableServerSideContext } from './TableServerSideContext';

const ColumnFilterSort = ({ align, header, searchHelperText, tableInstance }) => {
  const { columnSortModifiers, columnFilterModifiers } = useContext(TableServerSideContext);

  const [isEllipsisIconVisible, setIsEllipsisIconVisible] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [filterValue, setFilterValue] = useState('');

  const { sortBy = {}, setSortBy, column = {} } = tableInstance;

  const { setFilter, filterValue: colFilterValue } = column;

  const isSortAsc = sortBy[column.id] === 'asc';
  const isSortDesc = sortBy[column.id] === 'desc';
  const isFiltering = !!colFilterValue;
  const isSorting = Object.keys(sortBy)?.length > 0;
  const isFilteringOrSorting = isFiltering || isSorting;

  // Set Column filter as the user types
  const onFilterChange = e => {
    setFilterValue(e.target.value);
  };

  const closeIconAndMenu = () => {
    setIsEllipsisIconVisible(false);
    setIsMenuOpen(false);
  };

  // Set Column filter when user hits enter
  const onKeyDown = e => {
    if (e.keyCode === 13) {
      setFilter(filterValue);
      closeIconAndMenu();
    }
  };

  /* Clears text input if column filter is empty */
  useEffect(() => {
    if (!colFilterValue) {
      setFilterValue('');
    }
  }, [colFilterValue]);

  useEffect(() => {
    if (!isFilteringOrSorting) {
      closeIconAndMenu();
    }
  }, [isFilteringOrSorting]);

  const handleClearColumnSort = () => {
    // Remove the column id from sortBy object
    const { [column.id]: colId, ...withoutColId } = sortBy;
    setSortBy(withoutColId);
  };

  const handleClearColumnFilter = () => {
    setFilter('');
  };

  const toggleSortAsc = () => {
    if (isSortAsc) {
      handleClearColumnSort();
      return;
    }
    setSortBy({ ...sortBy, [column.id]: 'asc' });

    closeIconAndMenu();
  };

  const toggleSortDesc = () => {
    if (isSortDesc) {
      handleClearColumnSort();
      return;
    }

    setSortBy({ ...sortBy, [column.id]: 'desc' });

    // Hide menu
    setIsEllipsisIconVisible(false);
    setIsMenuOpen(false);
  };

  const onMouseEnterColumnHover = () => {
    setIsEllipsisIconVisible(true);
  };

  const onMouseLeaveColumnHover = () => {
    if (!isMenuOpen) {
      setIsEllipsisIconVisible(false);
    }
  };

  return (
    <Flex justifyContent='space-between' onMouseEnter={onMouseEnterColumnHover} onMouseLeave={onMouseLeaveColumnHover}>
      {header}

      <Flex>
        {isSortAsc && (
          <SortAscending
            style={{ cursor: 'pointer', color: sortHighlightColor }}
            onClick={toggleSortAsc}
            data-testid={`${header}-columnSortAscIcon`}
          />
        )}
        {isSortDesc && (
          <SortDescending
            style={{ cursor: 'pointer', color: sortHighlightColor }}
            onClick={toggleSortDesc}
            data-testid={`${header}-columnSortDescIcon`}
          />
        )}
        {isFiltering && (
          <Filter
            data-testid={`${header}-columnFilterIcon`}
            style={{ cursor: 'pointer', color: filterHightlightColor }}
            onClick={() => {
              setFilter('');
            }}
          />
        )}

        <ColumnFilterSortMenu
          align={align}
          isEllipsisIconVisible={isEllipsisIconVisible}
          isMenuOpen={isMenuOpen}
          setIsMenuOpen={setIsMenuOpen}
          toggleSortAsc={toggleSortAsc}
          toggleSortDesc={toggleSortDesc}
          onFilterChange={onFilterChange}
          onKeyDown={onKeyDown}
          disableSort={columnSortModifiers[column.id]?.disabled}
          disableFilter={columnFilterModifiers[column.id]?.disabled}
          filterValue={colFilterValue || filterValue}
          isFiltering={isFiltering}
          handleClearColumnFilter={handleClearColumnFilter}
          header={header}
          isSortAsc={isSortAsc}
          isSortDesc={isSortDesc}
          closeIconAndMenu={closeIconAndMenu}
          searchHelperText={searchHelperText}
        />
      </Flex>
    </Flex>
  );
};

ColumnFilterSort.propTypes = {
  align: string,
  header: string,
  searchHelperText: string,
  tableInstance: object.isRequired
};

ColumnFilterSort.defaultProps = {
  align: 'left',
  header: '',
  searchHelperText: ''
};

export default ColumnFilterSort;
